import React, { useState, useMemo, useRef } from 'react';
import { ShoppingCart, Plus, Edit, Trash2, Search, Save, X, PackageCheck, Send, Ban, Truck, Link2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';
import { makeDirtyClose } from '../lib/useDirtyClose';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export const STATUS_META = {
  concept: { label: 'Concept', cls: 'gray' },
  besteld: { label: 'Besteld', cls: 'blue' },
  deels_ontvangen: { label: 'Deels ontvangen', cls: 'amber' },
  ontvangen: { label: 'Ontvangen', cls: 'green' },
  geannuleerd: { label: 'Geannuleerd', cls: 'red' },
};

function statusBadge(s, t) {
  const m = STATUS_META[s] || STATUS_META.concept;
  return <span className={`cs-badge ${m.cls}`}>{t(m.label, m.label)}</span>;
}

function InkoopOrderEditor({ order, onClose }) {
  const { t } = useT();
  const { leveranciers, onderdelen, bonnen, createInkoopOrder, updateInkoopOrder } = useData();
  const [data, setData] = useState(() => order || {
    leverancierId: '', leverancierNaam: '', bonId: '', status: 'concept',
    datum: new Date().toISOString().slice(0, 10), notitie: '', regels: [],
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(data);
  const requestClose = makeDirtyClose(() => JSON.stringify(data) !== initialSig.current, onClose, t);

  const setLeverancier = (id) => {
    const l = (leveranciers || []).find(x => x.id === id);
    setData(d => ({ ...d, leverancierId: id, leverancierNaam: l ? l.naam : '' }));
  };

  const addRegel = () => setData(d => ({ ...d, regels: [...(d.regels || []), { id: `tmp_${Math.random().toString(36).slice(2, 8)}`, onderdeelId: '', code: '', omschrijving: '', aantal: 1, inkoopPrijs: 0, ontvangen: 0 }] }));
  const updRegel = (i, k, v) => setData(d => ({ ...d, regels: d.regels.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delRegel = (i) => setData(d => ({ ...d, regels: d.regels.filter((_, idx) => idx !== i) }));

  const pickOnderdeel = (i, oid) => {
    const o = (onderdelen || []).find(x => x.id === oid);
    setData(d => ({ ...d, regels: d.regels.map((r, idx) => idx === i ? {
      ...r, onderdeelId: oid,
      code: o ? (o.code || '') : r.code,
      omschrijving: o ? (o.omschrijving || o.naam || '') : r.omschrijving,
      inkoopPrijs: o && o.inkoop ? Number(o.inkoop) : r.inkoopPrijs,
    } : r) }));
  };

  const totaal = (data.regels || []).reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.inkoopPrijs) || 0), 0);

  const save = async (status) => {
    if (!data.leverancierId) { toast.error(t('Kies een leverancier', 'Kies een leverancier')); return; }
    if (!(data.regels || []).length) { toast.error(t('Voeg minstens 1 regel toe', 'Voeg minstens 1 regel toe')); return; }
    setSaving(true);
    try {
      const payload = { ...data, status: status || data.status };
      if (order?.id) await updateInkoopOrder(order.id, payload);
      else await createInkoopOrder(payload);
      toast.success(status === 'besteld' ? t('Order besteld', 'Order besteld') : t('Order opgeslagen', 'Order opgeslagen'));
      onClose();
    } catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const werkbonnen = (bonnen || []).filter(b => ['Werkbon', 'Offerte'].includes(b.type));

  return (
    <div className="cs-modal-overlay" onClick={requestClose}>
      <div className="cs-modal" style={{ maxWidth: 860 }} onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3><ShoppingCart size={16} /> {order?.id ? `${t('Inkooporder', 'Inkooporder')} ${order.nummer}` : t('Nieuwe inkooporder', 'Nieuwe inkooporder')}</h3>
          <button className="cs-btn ghost" onClick={requestClose}><X size={16} /></button>
        </div>
        <div className="cs-modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div><label className="cs-section-title">{t('Leverancier', 'Leverancier')} *</label>
              <select className="cs-input" value={data.leverancierId} onChange={e => setLeverancier(e.target.value)} data-testid="io-leverancier">
                <option value="">-- {t('Kies leverancier', 'Kies leverancier')} --</option>
                {(leveranciers || []).map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
              </select>
            </div>
            <div><label className="cs-section-title">{t('Datum')}</label>
              <input type="date" className="cs-input" value={data.datum} onChange={e => upd('datum', e.target.value)} /></div>
            <div><label className="cs-section-title">{t('Koppel aan werkbon', 'Koppel aan werkbon')}</label>
              <select className="cs-input" value={data.bonId || ''} onChange={e => upd('bonId', e.target.value)} data-testid="io-bon">
                <option value="">— {t('Geen', 'Geen')} —</option>
                {werkbonnen.map(b => <option key={b.id} value={b.id}>{b.nummer} · {b.kenteken || b.omschrijving}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label className="cs-section-title" style={{ margin: 0 }}>{t('Regels', 'Regels')}</label>
            <button className="cs-btn ghost" style={{ fontSize: 11 }} onClick={addRegel} data-testid="io-add-regel"><Plus size={11} /> {t('Regel toevoegen', 'Regel toevoegen')}</button>
          </div>
          <table className="cs-table">
            <thead><tr><th style={{ width: 180 }}>{t('Onderdeel uit voorraad', 'Onderdeel uit voorraad')}</th><th style={{ width: 90 }}>{t('Code')}</th><th>{t('Omschrijving')}</th><th style={{ width: 70 }}>{t('Aantal')}</th><th style={{ width: 100 }}>{t('Inkoopprijs', 'Inkoopprijs')}</th><th style={{ width: 90 }}>{t('Totaal')}</th><th style={{ width: 36 }}></th></tr></thead>
            <tbody>
              {(data.regels || []).map((r, i) => (
                <tr key={r.id || i}>
                  <td><select className="cs-input" style={{ padding: 4 }} value={r.onderdeelId || ''} onChange={e => pickOnderdeel(i, e.target.value)} data-testid={`io-regel-onderdeel-${i}`}>
                    <option value="">— {t('Vrije regel', 'Vrije regel')} —</option>
                    {(onderdelen || []).map(o => <option key={o.id} value={o.id}>{o.code ? o.code + ' · ' : ''}{o.omschrijving || o.naam}</option>)}
                  </select></td>
                  <td><input className="cs-input" style={{ padding: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={r.code || ''} onChange={e => updRegel(i, 'code', e.target.value)} /></td>
                  <td><input className="cs-input" style={{ padding: 4 }} value={r.omschrijving || ''} onChange={e => updRegel(i, 'omschrijving', e.target.value)} /></td>
                  <td><input type="number" className="cs-input" style={{ padding: 4, textAlign: 'right' }} value={r.aantal} onChange={e => updRegel(i, 'aantal', e.target.value)} /></td>
                  <td><input type="number" step="0.01" className="cs-input" style={{ padding: 4, textAlign: 'right' }} value={r.inkoopPrijs} onChange={e => updRegel(i, 'inkoopPrijs', e.target.value)} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur((Number(r.aantal) || 0) * (Number(r.inkoopPrijs) || 0))}</td>
                  <td><button className="cs-btn ghost danger" onClick={() => delRegel(i)}><Trash2 size={11} /></button></td>
                </tr>
              ))}
              {(data.regels || []).length === 0 && <tr><td colSpan={7} className="cs-empty">{t('Voeg regels toe via de knop hierboven.', 'Voeg regels toe via de knop hierboven.')}</td></tr>}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 10, gap: 12 }}>
            <div style={{ flex: 1 }}><label className="cs-section-title">{t('Notitie')}</label>
              <input className="cs-input" value={data.notitie} onChange={e => upd('notitie', e.target.value)} /></div>
            <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 800 }}>{t('Totaal')}: {eur(totaal)}</div>
          </div>
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={requestClose}>{t('Annuleren')}</button>
          <button className="cs-btn" onClick={() => save('concept')} disabled={saving} data-testid="io-save-concept"><Save size={14} /> {t('Opslaan als concept', 'Opslaan als concept')}</button>
          <button className="cs-btn primary" onClick={() => save('besteld')} disabled={saving} data-testid="io-save-besteld"><Send size={14} /> {t('Bestellen', 'Bestellen')}</button>
        </div>
      </div>
    </div>
  );
}

function OntvangstModal({ order, onClose }) {
  const { t } = useT();
  const { ontvangstInkoopOrder } = useData();
  const [regels, setRegels] = useState(() => (order.regels || []).map(r => ({ ...r, ontvangen: Number(r.ontvangen) || 0 })));
  const [saving, setSaving] = useState(false);
  const setOntvangen = (i, v) => setRegels(rs => rs.map((r, idx) => idx === i ? { ...r, ontvangen: v } : r));
  const allesOntvangen = () => setRegels(rs => rs.map(r => ({ ...r, ontvangen: Number(r.aantal) || 0 })));

  const confirm = async () => {
    setSaving(true);
    try {
      const res = await ontvangstInkoopOrder(order.id, { regels: regels.map(r => ({ id: r.id, ontvangen: Number(r.ontvangen) || 0 })) });
      toast.success(res.status === 'ontvangen' ? t('Volledig ontvangen — voorraad bijgewerkt', 'Volledig ontvangen — voorraad bijgewerkt') : t('Deelontvangst geboekt', 'Deelontvangst geboekt'));
      if (res.kostenpostAangemaakt) toast.success(t('Kostenpost automatisch aangemaakt in Boekhouden', 'Kostenpost automatisch aangemaakt in Boekhouden'));
      if (res.nieuweArtikelen > 0) toast.success(`${res.nieuweArtikelen} ${t('nieuw(e) artikel(en) aangemaakt in voorraad', 'nieuw(e) artikel(en) aangemaakt in voorraad')}`);
      onClose();
    } catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3><PackageCheck size={16} /> {t('Ontvangst boeken', 'Ontvangst boeken')} — {order.nummer}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="cs-modal-body">
          <p style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginTop: 0 }}>{t('Vul in hoeveel er in totaal ontvangen is. Gekoppelde artikelen worden verhoogd; vrije regels worden automatisch als nieuw voorraad-artikel aangemaakt.', 'Vul in hoeveel er in totaal ontvangen is. Gekoppelde artikelen worden verhoogd; vrije regels worden automatisch als nieuw voorraad-artikel aangemaakt.')}</p>
          <table className="cs-table">
            <thead><tr><th>{t('Omschrijving')}</th><th style={{ width: 80, textAlign: 'right' }}>{t('Besteld', 'Besteld')}</th><th style={{ width: 110, textAlign: 'right' }}>{t('Ontvangen', 'Ontvangen')}</th></tr></thead>
            <tbody>
              {regels.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{r.code ? <b style={{ fontFamily: 'ui-monospace, monospace' }}>{r.code} </b> : ''}{r.omschrijving}{!r.onderdeelId && <span style={{ fontSize: 10.5, color: '#94a3b8' }}> · {t('geen voorraadkoppeling', 'geen voorraadkoppeling')}</span>}</td>
                  <td style={{ textAlign: 'right' }}>{r.aantal}</td>
                  <td><input type="number" min="0" max={r.aantal} className="cs-input" style={{ padding: 4, textAlign: 'right' }} value={r.ontvangen} onChange={e => setOntvangen(i, e.target.value)} data-testid={`io-ontvangst-${i}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="cs-btn ghost" style={{ marginTop: 8, fontSize: 12 }} onClick={allesOntvangen} data-testid="io-alles-ontvangen">{t('Alles ontvangen', 'Alles ontvangen')}</button>
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={confirm} disabled={saving} data-testid="io-ontvangst-confirm"><PackageCheck size={14} /> {t('Ontvangst bevestigen', 'Ontvangst bevestigen')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Inkooporders() {
  const { t } = useT();
  const { inkoopOrders, bonnen, deleteInkoopOrder, setInkoopOrderStatus } = useData();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [editing, setEditing] = useState(null);
  const [ontvangst, setOntvangst] = useState(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (inkoopOrders || []).filter(o => {
      const matchF = !f || (o.nummer || '').toLowerCase().includes(f) || (o.leverancierNaam || '').toLowerCase().includes(f);
      const matchS = statusFilter === 'Alle' || o.status === statusFilter;
      return matchF && matchS;
    });
  }, [inkoopOrders, filter, statusFilter]);

  const bonNummer = (bid) => (bonnen || []).find(b => b.id === bid)?.nummer;

  const remove = async (o) => {
    if (!(await confirmDialog(`${t('Inkooporder verwijderen?', 'Inkooporder verwijderen?')} ${o.nummer}`))) return;
    try { await deleteInkoopOrder(o.id); toast.success(t('Verwijderd', 'Verwijderd')); }
    catch { toast.error(t('Mislukt')); }
  };
  const annuleer = async (o) => {
    if (!(await confirmDialog(`${t('Order annuleren?', 'Order annuleren?')} ${o.nummer}`))) return;
    try { await setInkoopOrderStatus(o.id, { status: 'geannuleerd' }); toast.success(t('Geannuleerd', 'Geannuleerd')); }
    catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail }); }
  };
  const bestel = async (o) => {
    try { await setInkoopOrderStatus(o.id, { status: 'besteld' }); toast.success(t('Besteld', 'Besteld')); }
    catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail }); }
  };

  const openVoorraad = (inkoopOrders || []).filter(o => ['besteld', 'deels_ontvangen'].includes(o.status));
  const openWaarde = openVoorraad.reduce((s, o) => s + (Number(o.totaal) || 0), 0);

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('Inkooporders', 'Inkooporders')}</h2><p>{(inkoopOrders || []).length} {t('orders', 'orders')}</p></div>
        <button className="cs-btn primary" onClick={() => setEditing({})} data-testid="btn-io-add"><Plus size={14} /> {t('Nieuwe inkooporder', 'Nieuwe inkooporder')}</button>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi"><div className="label">{t('Openstaande orders', 'Openstaande orders')}</div><div className="value">{openVoorraad.length}</div></div>
        <div className="cs-kpi"><div className="label">{t('Waarde onderweg', 'Waarde onderweg')}</div><div className="value">{eur(openWaarde)}</div></div>
        <div className="cs-kpi"><div className="label">{t('Ontvangen', 'Ontvangen')}</div><div className="value">{(inkoopOrders || []).filter(o => o.status === 'ontvangen').length}</div></div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek ordernr of leverancier...', 'Zoek ordernr of leverancier...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {['Alle', 'concept', 'besteld', 'deels_ontvangen', 'ontvangen', 'geannuleerd'].map(s => (
            <button key={s} className="cs-btn ghost" style={{ padding: '4px 10px', fontSize: 11, background: statusFilter === s ? 'var(--cs-accent-soft)' : 'transparent', color: statusFilter === s ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setStatusFilter(s)}>{s === 'Alle' ? t('Alle', 'Alle') : t(STATUS_META[s].label, STATUS_META[s].label)}</button>
          ))}
        </div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr><th>{t('Nummer')}</th><th>{t('Datum')}</th><th>{t('Leverancier', 'Leverancier')}</th><th>{t('Werkbon', 'Werkbon')}</th><th>{t('Regels', 'Regels')}</th><th style={{ textAlign: 'right' }}>{t('Totaal')}</th><th>{t('Status')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(o => {
              const canEdit = ['concept', 'besteld'].includes(o.status);
              const canReceive = ['besteld', 'deels_ontvangen'].includes(o.status);
              return (
                <tr key={o.id} onDoubleClick={() => canEdit && setEditing(o)} data-testid={`io-row-${o.id}`}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{o.nummer}</td>
                  <td>{o.datum ? new Date(o.datum).toLocaleDateString('nl-NL') : '-'}</td>
                  <td><Truck size={12} /> {o.leverancierNaam || '-'}</td>
                  <td>{o.bonId ? <span className="cs-badge gray"><Link2 size={10} /> {bonNummer(o.bonId) || o.bonId}</span> : '-'}</td>
                  <td>{(o.regels || []).length}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(o.totaal)}</td>
                  <td>{statusBadge(o.status, t)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {o.status === 'concept' && <button className="cs-btn ghost" title={t('Bestellen', 'Bestellen')} onClick={() => bestel(o)} data-testid={`io-bestel-${o.id}`}><Send size={12} /></button>}
                    {canReceive && <button className="cs-btn ghost" style={{ color: 'var(--cs-green)' }} title={t('Ontvangst boeken', 'Ontvangst boeken')} onClick={() => setOntvangst(o)} data-testid={`io-receive-${o.id}`}><PackageCheck size={12} /></button>}
                    {canEdit && <button className="cs-btn ghost" title={t('Bewerken')} onClick={() => setEditing(o)}><Edit size={12} /></button>}
                    {canEdit && <button className="cs-btn ghost" style={{ color: 'var(--cs-amber)' }} title={t('Annuleren')} onClick={() => annuleer(o)}><Ban size={12} /></button>}
                    <button className="cs-btn ghost danger" title={t('Verwijderen')} onClick={() => remove(o)} data-testid={`io-delete-${o.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="cs-empty">{t('Nog geen inkooporders.', 'Nog geen inkooporders.')}</div>}
      </div>

      {editing && <InkoopOrderEditor order={editing.id ? editing : null} onClose={() => setEditing(null)} />}
      {ontvangst && <OntvangstModal order={ontvangst} onClose={() => setOntvangst(null)} />}
    </div>
  );
}
